import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import {
  shares as sharesApi,
  prospects as prospectsApi,
  documents as documentsApi,
} from "@/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Requests from "@/pages/Requests";
import { SortableTableHeader } from "@/components/ui/SortableTableHeader";
import { useTableSort } from "@/hooks/useTableSort";
import {
  Eye,
  Clock,
  ArrowSquareOut,
  FileText,
  UserList,
  UserCircle,
  PencilSimple,
  Trash,
  CalendarBlank,
  ChartLineUp,
  DotsThreeVertical,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ActaActionMenu,
  ActaActionMenuItem,
  ActaActionSheet,
} from "@/components/ui/ActaActionMenu";

const MOBILE_PAGE_SIZE = 3;
const DESKTOP_PAGE_SIZE = 7;

function getStatsPageSize() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return DESKTOP_PAGE_SIZE;
  }

  return window.matchMedia("(min-width: 768px)").matches
    ? DESKTOP_PAGE_SIZE
    : MOBILE_PAGE_SIZE;
}

function fmtSeconds(total) {
  if (!total) return "0s";
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isoToDateInput(iso) {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 10);
}

function safeLength(value) {
  return Array.isArray(value) ? value.length : 0;
}

function statusForShare(share) {
  if (share.deleted) return { label: "Supprimé", muted: true };
  if (share.revoked) return { label: "Révoqué", muted: true };
  if (share.expiresAt && new Date(share.expiresAt) < new Date()) {
    return { label: "Expiré", muted: false };
  }
  if (
    Number(share.maxOpens || 0) > 0 &&
    Number(share.openCount || 0) >= Number(share.maxOpens || 0)
  ) {
    return { label: "Épuisé", muted: true };
  }
  return { label: "Actif", muted: false };
}

function buildQueryFilters(filters) {
  const query = {};
  if (filters.shareId) query.share_id = filters.shareId;
  if (filters.prospectId) query.prospect_id = filters.prospectId;
  if (filters.ownerUserId) query.owner_user_id = filters.ownerUserId;
  if (filters.documentId) query.document_id = filters.documentId;
  if (filters.period) query.period = filters.period;
  if (filters.since) query.since = filters.since;
  if (filters.until) query.until = filters.until;
  return query;
}

export default function Stats() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const selectedDocumentId = searchParams.get("documentId") || "";
  const selectedShareId =
    searchParams.get("share_id") || searchParams.get("shareId") || "";

  const isAdminLike = user.role === "admin" || user.role === "manager";

  const [shares, setShares] = useState([]);
  const [sharesTotal, setSharesTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileFilterView, setMobileFilterView] = useState("root");
  const [documentSearch, setDocumentSearch] = useState("");
  const [filters, setFilters] = useState({
    shareId: selectedShareId,
    prospectId: "",
    ownerUserId: "",
    documentId: selectedDocumentId,
    period: selectedShareId ? "all" : "month",
    since: "",
    until: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStatsPageSize);
  const [loadingRows, setLoadingRows] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const { sortBy, sortOrder, toggleSort } = useTableSort("createdAt", "desc");

  const [editingShare, setEditingShare] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [editMaxOpens, setEditMaxOpens] = useState(5);
  const [editExpirationDate, setEditExpirationDate] = useState("");
  const [actionMenuId, setActionMenuId] = useState("");
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [desktopActionShare, setDesktopActionShare] = useState(null);
  const [mobileActionShare, setMobileActionShare] = useState(null);
  const [deleteShareTarget, setDeleteShareTarget] = useState(null);
  const [deletingShare, setDeletingShare] = useState(false);

  const updateFilters = (changes) => {
    setPage(1);
    setFilters((current) => ({ ...current, ...changes }));
  };

  const loadFilterOptions = async () => {
    if (!isAdminLike) return;
    try {
      const [prospectRows, commercialRows, documentRows] = await Promise.all([
        prospectsApi.list(),
        prospectsApi.listCommercialUsers(),
        documentsApi.list(),
      ]);
      setProspects(Array.isArray(prospectRows) ? prospectRows : []);
      setUsers(Array.isArray(commercialRows) ? commercialRows : []);
      setDocuments(Array.isArray(documentRows) ? documentRows : []);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const loadRows = async () => {
    setLoadingRows(true);
    try {
      const data = await sharesApi.statsPage({
        ...buildQueryFilters(filters),
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setShares(Array.isArray(data?.items) ? data.items : []);
      setSharesTotal(Number(data?.total || 0));
      const pages = Math.max(1, Number(data?.totalPages || 1));
      setTotalPages(pages);
      if (page > pages) setPage(pages);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoadingRows(false);
    }
  };

  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const data = await sharesApi.statsSummary(buildQueryFilters(filters));
      setSummary(data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const media = window.matchMedia("(min-width: 768px)");

    const handlePageSizeChange = () => {
      const nextPageSize = media.matches
        ? DESKTOP_PAGE_SIZE
        : MOBILE_PAGE_SIZE;

      setPageSize((current) => {
        if (current === nextPageSize) return current;
        setPage(1);
        return nextPageSize;
      });
    };

    if (media.addEventListener) {
      media.addEventListener("change", handlePageSizeChange);
      return () => media.removeEventListener("change", handlePageSizeChange);
    }

    media.addListener(handlePageSizeChange);
    return () => media.removeListener(handlePageSizeChange);
  }, []);

  useEffect(() => {
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminLike]);

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    setFilters((current) => ({
      ...current,
      documentId: selectedDocumentId,
    }));
    setPage(1);
  }, [selectedDocumentId]);

  useEffect(() => {
    setFilters((current) => ({
      ...current,
      shareId: selectedShareId,
      period: selectedShareId ? "all" : current.period,
    }));
    setPage(1);
  }, [selectedShareId]);

  const refreshAfterAction = async () => {
    await Promise.all([loadRows(), loadSummary()]);
  };

  const handleShareSort = (field) => {
    toggleSort(field);
    setPage(1);
  };

  const closeActionMenus = () => {
    setActionMenuId("");
    setActionAnchorEl(null);
    setDesktopActionShare(null);
    setMobileActionShare(null);
  };

  const openActionMenu = (event, share) => {
    event.stopPropagation();
    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    if (isMobile) {
      setActionMenuId("");
      setActionAnchorEl(null);
      setDesktopActionShare(null);
      setMobileActionShare(share);
      return;
    }

    if (actionMenuId === share.id) {
      closeActionMenus();
      return;
    }

    setMobileActionShare(null);
    setActionMenuId(share.id);
    setActionAnchorEl(event.currentTarget);
    setDesktopActionShare(share);
  };

  const openOpensDialog = (share) => {
    setEditingShare(share);
    setEditMode("opens");
    setEditMaxOpens(share.maxOpens || 5);
  };

  const openExpirationDialog = (share) => {
    setEditingShare(share);
    setEditMode("expiration");
    setEditExpirationDate(isoToDateInput(share.expiresAt));
  };

  const closeEditDialog = () => {
    setEditingShare(null);
    setEditMode(null);
  };

  const saveEdit = async () => {
    if (!editingShare) return;
    try {
      if (editMode === "opens") {
        await sharesApi.updateOpens(editingShare.id, {
          max_opens: Number(editMaxOpens),
        });
        toast.success("Limite d'ouvertures modifiée");
      } else if (editMode === "expiration") {
        const expiresAt = new Date(
          `${editExpirationDate}T23:59:59.000Z`
        ).toISOString();
        await sharesApi.updateExpiration(editingShare.id, {
          expires_at: expiresAt,
        });
        toast.success("Expiration modifiée");
      }

      closeEditDialog();
      await refreshAfterAction();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const removeShare = async (share) => {
    if (!share || deletingShare) return;
    setDeletingShare(true);
    try {
      await sharesApi.remove(share.id);
      toast.success("Lien supprimé, historique conservé");
      setDeleteShareTarget(null);
      await refreshAfterAction();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setDeletingShare(false);
    }
  };

  const kpiCount = isAdminLike ? 5 : 4;
  const kpiGridClass = isAdminLike
    ? "grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-5"
    : "grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4";

  return (
    <div
      className="w-full px-4 py-4 sm:px-6 lg:p-8 acta-standard-page acta-commerce-page-standard"
      data-testid="stats-page"
    >
      <AlertDialog
        open={Boolean(deleteShareTarget)}
        onOpenChange={(open) => {
          if (!open && !deletingShare) setDeleteShareTarget(null);
        }}
      >
        <AlertDialogContent data-testid="stats-delete-share-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce lien ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le lien disparaîtra de la liste, mais son historique et son statut seront conservés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingShare}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={deletingShare}
              onClick={(event) => {
                event.preventDefault();
                removeShare(deleteShareTarget);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingShare ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <section
        className="mb-5 space-y-3 sm:mb-6 sm:space-y-4"
        data-testid="stats-filters"
      >
        {loadingSummary || !summary ? (
          <div className={kpiGridClass} aria-label="Chargement des indicateurs">
            {Array.from({ length: kpiCount }).map((_, index) => (
              <div key={index} className="au-card h-24 lirah-pulse" />
            ))}
          </div>
        ) : (
          <section className={kpiGridClass} data-testid="stats-kpi-grid">
            <MetricCard
              label={isAdminLike ? "Partages actifs" : "Mes partages actifs"}
              value={summary.totals?.shares || 0}
              detail="Consultations"
              icon={ChartLineUp}
            />
            <MetricCard
              label={isAdminLike ? "Taux d’ouverture" : "Mon taux d’ouverture"}
              value={`${summary.totals?.openRate || 0}%`}
              detail="Consultations"
              icon={Eye}
            />
            <MetricCard
              label={
                isAdminLike
                  ? "Document le plus consulté"
                  : "Mon document le plus consulté"
              }
              value={summary.topDocument?.name || "—"}
              detail="Document principal"
              icon={FileText}
            />
            <MetricCard
              label={
                isAdminLike
                  ? "Prospect le plus engagé"
                  : "Mon prospect le plus engagé"
              }
              value={summary.topProspect?.name || "—"}
              detail="Meilleur engagement"
              icon={UserList}
            />
            {isAdminLike ? (
              <MetricCard
                label="Commercial le plus performant"
                value={summary.topOwner?.name || "—"}
                detail="Performance"
                icon={UserCircle}
                className="col-span-2 lg:col-span-1"
              />
            ) : null}
          </section>
        )}

        {isAdminLike ? (
          <div className="w-full" data-testid="stats-toolbar">

            {/* MOBILE */}
            <div className="md:hidden">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMobileFilterView("period");
                    setMobileFiltersOpen(true);
                  }}
                  className="flex h-9 min-w-0 items-center justify-between rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-left text-xs font-semibold text-[var(--do-ink)]"
                  data-testid="stats-mobile-period"
                >
                  <span className="truncate">
                    {{
                      month: "Ce mois-ci",
                      "3m": "3 derniers mois",
                      "6m": "6 derniers mois",
                      "12m": "12 derniers mois",
                      all: "Toutes les périodes",
                      custom: "Dates personnalisées",
                    }[filters.period] || "Période"}
                  </span>

                  <span className="ml-2 text-[var(--do-muted)]">›</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMobileFilterView("root");
                    setMobileFiltersOpen(true);
                  }}
                  className="h-9 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-xs font-bold text-[var(--do-ink)]"
                  data-testid="stats-mobile-filters-toggle"
                >
                  Filtres
                </button>
              </div>

              {mobileFiltersOpen ? (
                <div
                  className="mt-2 overflow-hidden rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-[var(--do-shadow-card)]"
                  data-testid="stats-mobile-filters"
                >
                  <div className="flex h-11 items-center justify-between border-b border-[var(--do-border)] px-3">
                    <div className="flex min-w-0 items-center gap-2">
                      {mobileFilterView !== "root" ? (
                        <button
                          type="button"
                          onClick={() => setMobileFilterView("root")}
                          className="shrink-0 text-sm font-bold text-[var(--do-accent)]"
                        >
                          ‹
                        </button>
                      ) : null}

                      <span className="truncate text-sm font-bold text-[var(--do-ink)]">
                        {mobileFilterView === "period"
                          ? "Période"
                          : mobileFilterView === "owner"
                          ? "Commercial"
                          : mobileFilterView === "prospect"
                          ? "Prospect"
                          : mobileFilterView === "document"
                          ? "Document"
                          : "Filtres"}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setMobileFiltersOpen(false);
                        setMobileFilterView("root");
                      }}
                      className="shrink-0 text-xs font-semibold text-[var(--do-muted)]"
                    >
                      Fermer
                    </button>
                  </div>

                  {mobileFilterView === "root" ? (
                    <div className="p-2">
                      <button
                        type="button"
                        onClick={() => setMobileFilterView("owner")}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-[var(--do-bg-2)]"
                      >
                        <span className="text-xs text-[var(--do-muted)]">
                          Commercial
                        </span>

                        <span className="min-w-0 truncate text-xs font-semibold text-[var(--do-ink)]">
                          {filters.ownerUserId
                            ? users.find(
                                (commercial) =>
                                  commercial.id === filters.ownerUserId
                              )?.name ||
                              (() => {
                                const commercial = users.find(
                                  (item) =>
                                    item.id === filters.ownerUserId
                                );

                                return commercial
                                  ? `${commercial.firstName || ""} ${
                                      commercial.lastName || ""
                                    }`.trim() || commercial.email
                                  : "Commercial";
                              })()
                            : user.role === "manager"
                            ? "Mon équipe"
                            : "Tous"}
                          {" ›"}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setMobileFilterView("prospect")}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-[var(--do-bg-2)]"
                      >
                        <span className="text-xs text-[var(--do-muted)]">
                          Prospect
                        </span>

                        <span className="min-w-0 truncate text-xs font-semibold text-[var(--do-ink)]">
                          {filters.prospectId
                            ? (() => {
                                const prospect = prospects.find(
                                  (item) => item.id === filters.prospectId
                                );

                                return prospect
                                  ? `${prospect.firstName || ""} ${
                                      prospect.lastName || ""
                                    }`.trim()
                                  : "Prospect";
                              })()
                            : "Tous"}
                          {" ›"}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setMobileFilterView("document")}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-[var(--do-bg-2)]"
                      >
                        <span className="text-xs text-[var(--do-muted)]">
                          Document
                        </span>

                        <span className="max-w-[60%] truncate text-xs font-semibold text-[var(--do-ink)]">
                          {filters.documentId
                            ? documents.find(
                                (document) =>
                                  document.id === filters.documentId
                              )?.name || "Document"
                            : "Tous"}
                          {" ›"}
                        </span>
                      </button>

                      {filters.period === "custom" ? (
                        <div className="mt-1 grid grid-cols-2 gap-2 border-t border-[var(--do-border)] p-2 pt-3">
                          <input
                            type="date"
                            value={filters.since}
                            onChange={(event) =>
                              updateFilters({
                                since: event.target.value,
                              })
                            }
                            className="h-9 min-w-0 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2 text-xs"
                          />

                          <input
                            type="date"
                            value={filters.until}
                            onChange={(event) =>
                              updateFilters({
                                until: event.target.value,
                              })
                            }
                            className="h-9 min-w-0 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2 text-xs"
                          />
                        </div>
                      ) : null}

                      <div className="mt-1 border-t border-[var(--do-border)] pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            updateFilters({
                              period: "month",
                              ownerUserId: "",
                              prospectId: "",
                              documentId: "",
                              since: "",
                              until: "",
                            });
                            setDocumentSearch("");
                          }}
                          className="w-full rounded-lg px-3 py-2 text-xs font-semibold text-[var(--do-muted)]"
                        >
                          Réinitialiser les filtres
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {mobileFilterView === "period" ? (
                    <div className="max-h-64 overflow-y-auto p-2">
                      {[
                        ["month", "Ce mois-ci"],
                        ["3m", "3 derniers mois"],
                        ["6m", "6 derniers mois"],
                        ["12m", "12 derniers mois"],
                        ["all", "Toutes les périodes"],
                        ["custom", "Dates personnalisées"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            updateFilters({
                              period: value,
                              since:
                                value === "custom" ? filters.since : "",
                              until:
                                value === "custom" ? filters.until : "",
                            });

                            if (value === "custom") {
                              setMobileFilterView("root");
                            } else {
                              setMobileFiltersOpen(false);
                              setMobileFilterView("root");
                            }
                          }}
                          className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs ${
                            filters.period === value
                              ? "bg-[var(--do-accent-soft)] font-bold text-[var(--do-accent)]"
                              : "text-[var(--do-ink)]"
                          }`}
                        >
                          <span>{label}</span>
                          {filters.period === value ? <span>✓</span> : null}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {mobileFilterView === "owner" ? (
                    <div className="max-h-64 overflow-y-auto p-2">
                      <button
                        type="button"
                        onClick={() => {
                          updateFilters({ ownerUserId: "" });
                          setMobileFilterView("root");
                        }}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs ${
                          !filters.ownerUserId
                            ? "bg-[var(--do-accent-soft)] font-bold text-[var(--do-accent)]"
                            : "text-[var(--do-ink)]"
                        }`}
                      >
                        <span>
                          {user.role === "manager"
                            ? "Mon équipe"
                            : "Tous les commerciaux"}
                        </span>

                        {!filters.ownerUserId ? <span>✓</span> : null}
                      </button>

                      {users.map((commercial) => {
                        const label =
                          commercial.name ||
                          `${commercial.firstName || ""} ${
                            commercial.lastName || ""
                          }`.trim() ||
                          commercial.email;

                        return (
                          <button
                            key={commercial.id}
                            type="button"
                            onClick={() => {
                              updateFilters({
                                ownerUserId: commercial.id,
                              });
                              setMobileFilterView("root");
                            }}
                            className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs ${
                              filters.ownerUserId === commercial.id
                                ? "bg-[var(--do-accent-soft)] font-bold text-[var(--do-accent)]"
                                : "text-[var(--do-ink)]"
                            }`}
                          >
                            <span className="truncate">{label}</span>
                            {filters.ownerUserId === commercial.id ? (
                              <span>✓</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  {mobileFilterView === "prospect" ? (
                    <div className="max-h-64 overflow-y-auto p-2">
                      <button
                        type="button"
                        onClick={() => {
                          updateFilters({ prospectId: "" });
                          setMobileFilterView("root");
                        }}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs ${
                          !filters.prospectId
                            ? "bg-[var(--do-accent-soft)] font-bold text-[var(--do-accent)]"
                            : "text-[var(--do-ink)]"
                        }`}
                      >
                        <span>Tous les prospects</span>
                        {!filters.prospectId ? <span>✓</span> : null}
                      </button>

                      {prospects.map((prospect) => {
                        const label =
                          `${prospect.firstName || ""} ${
                            prospect.lastName || ""
                          }`.trim() ||
                          prospect.company ||
                          "Prospect";

                        return (
                          <button
                            key={prospect.id}
                            type="button"
                            onClick={() => {
                              updateFilters({
                                prospectId: prospect.id,
                              });
                              setMobileFilterView("root");
                            }}
                            className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs ${
                              filters.prospectId === prospect.id
                                ? "bg-[var(--do-accent-soft)] font-bold text-[var(--do-accent)]"
                                : "text-[var(--do-ink)]"
                            }`}
                          >
                            <span className="truncate">{label}</span>
                            {filters.prospectId === prospect.id ? (
                              <span>✓</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  {mobileFilterView === "document" ? (
                    <div className="p-2">
                      <input
                        type="search"
                        value={documentSearch}
                        onChange={(event) =>
                          setDocumentSearch(event.target.value)
                        }
                        placeholder="Rechercher un document"
                        className="h-9 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-xs"
                        data-testid="stats-mobile-document-search"
                      />

                      <div className="mt-2 max-h-56 overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => {
                            updateFilters({ documentId: "" });
                            setDocumentSearch("");
                            setMobileFilterView("root");
                          }}
                          className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs ${
                            !filters.documentId
                              ? "bg-[var(--do-accent-soft)] font-bold text-[var(--do-accent)]"
                              : "text-[var(--do-ink)]"
                          }`}
                        >
                          <span>Tous les documents</span>
                          {!filters.documentId ? <span>✓</span> : null}
                        </button>

                        {documents
                          .filter((document) =>
                            (document.name || "")
                              .toLowerCase()
                              .includes(
                                documentSearch.trim().toLowerCase()
                              )
                          )
                          .slice(0, 10)
                          .map((document) => (
                            <button
                              key={document.id}
                              type="button"
                              onClick={() => {
                                updateFilters({
                                  documentId: document.id,
                                });
                                setDocumentSearch("");
                                setMobileFilterView("root");
                              }}
                              className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs ${
                                filters.documentId === document.id
                                  ? "bg-[var(--do-accent-soft)] font-bold text-[var(--do-accent)]"
                                  : "text-[var(--do-ink)]"
                              }`}
                            >
                              <span className="truncate">
                                {document.name}
                              </span>

                              {filters.documentId === document.id ? (
                                <span>✓</span>
                              ) : null}
                            </button>
                          ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {/* DESKTOP */}
            <div className="hidden md:flex md:gap-2">
              <select
                value={filters.period}
                onChange={(event) =>
                  updateFilters({
                    period: event.target.value,
                    since:
                      event.target.value === "custom"
                        ? filters.since
                        : "",
                    until:
                      event.target.value === "custom"
                        ? filters.until
                        : "",
                  })
                }
                data-testid="filter-period"
                className="h-9 min-w-[150px] flex-1 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 text-xs"
              >
                <option value="month">Ce mois-ci</option>
                <option value="3m">3 derniers mois</option>
                <option value="6m">6 derniers mois</option>
                <option value="12m">12 derniers mois</option>
                <option value="all">Toutes les périodes</option>
                <option value="custom">Dates personnalisées</option>
              </select>

              <select
                value={filters.ownerUserId}
                onChange={(event) =>
                  updateFilters({ ownerUserId: event.target.value })
                }
                data-testid="filter-owner"
                className="h-9 min-w-[170px] flex-1 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 text-xs"
              >
                <option value="">
                  {user.role === "manager"
                    ? "Mon équipe"
                    : "Tous les commerciaux"}
                </option>

                {users.map((commercial) => (
                  <option key={commercial.id} value={commercial.id}>
                    {commercial.name ||
                      `${commercial.firstName || ""} ${
                        commercial.lastName || ""
                      }`.trim() ||
                      commercial.email}
                  </option>
                ))}
              </select>

              <select
                value={filters.prospectId}
                onChange={(event) =>
                  updateFilters({ prospectId: event.target.value })
                }
                data-testid="filter-prospect"
                className="h-9 min-w-[160px] flex-1 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 text-xs"
              >
                <option value="">Tous les prospects</option>

                {prospects.map((prospect) => (
                  <option key={prospect.id} value={prospect.id}>
                    {prospect.firstName} {prospect.lastName}
                    {prospect.company ? ` · ${prospect.company}` : ""}
                  </option>
                ))}
              </select>

              <select
                value={filters.documentId}
                onChange={(event) =>
                  updateFilters({ documentId: event.target.value })
                }
                data-testid="filter-document"
                className="h-9 min-w-[170px] flex-1 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 text-xs"
              >
                <option value="">Tous les documents</option>

                {documents.map((document) => (
                  <option key={document.id} value={document.id}>
                    {document.name}
                  </option>
                ))}
              </select>
            </div>

            {filters.period === "custom" ? (
              <div className="mt-2 hidden gap-2 md:flex">
                <input
                  type="date"
                  value={filters.since}
                  onChange={(event) =>
                    updateFilters({ since: event.target.value })
                  }
                  data-testid="filter-since"
                  className="h-9 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 text-xs"
                />

                <input
                  type="date"
                  value={filters.until}
                  onChange={(event) =>
                    updateFilters({ until: event.target.value })
                  }
                  data-testid="filter-until"
                  className="h-9 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 text-xs"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="mb-6 sm:mb-8">

        {loadingRows && shares.length === 0 ? (
          <div
            className="au-card overflow-hidden"
            aria-label="Chargement du tableau"
          >
            {/* Skeleton desktop : même structure que les 7 lignes finales */}
            <div className="hidden md:block">
              <div className="border-b border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3">
                <div className="grid grid-cols-[1.4fr_1.3fr_.7fr_1fr_1fr_.7fr_.5fr] gap-4">
                  {Array.from({ length: 7 }, (_, index) => (
                    <div
                      key={index}
                      className="h-3 rounded bg-[var(--do-bg-2)] lirah-pulse"
                    />
                  ))}
                </div>
              </div>

              {Array.from({ length: 7 }, (_, row) => (
                <div
                  key={row}
                  className="grid min-h-[72px] grid-cols-[1.4fr_1.3fr_.7fr_1fr_1fr_.7fr_.5fr] items-center gap-4 border-b border-[var(--do-border)] px-4 py-4"
                >
                  <div className="space-y-2">
                    <div className="h-4 w-28 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                    <div className="h-3 w-20 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                  </div>

                  <div className="space-y-2">
                    <div className="h-2 w-full rounded-full bg-[var(--do-bg-2)] lirah-pulse" />
                    <div className="h-3 w-24 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                  </div>

                  <div className="mx-auto h-4 w-6 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                  <div className="h-4 w-16 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                  <div className="h-4 w-20 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                  <div className="h-6 w-14 rounded-full bg-[var(--do-bg-2)] lirah-pulse" />
                  <div className="ml-auto h-9 w-9 rounded-lg bg-[var(--do-bg-2)] lirah-pulse" />
                </div>
              ))}
            </div>

            {/* Skeleton mobile : même hauteur que les 3 cartes finales */}
            <div className="space-y-2 p-2.5 md:hidden">
              {Array.from({ length: 3 }, (_, item) => (
                <div
                  key={item}
                  className="rounded-[14px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1.5">
                      <div className="h-4 w-28 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                      <div className="h-3 w-20 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                    </div>

                    <div className="h-5 w-12 rounded-full bg-[var(--do-bg-2)] lirah-pulse" />
                  </div>

                  <div className="mt-2.5">
                    <div className="flex items-center justify-between">
                      <div className="h-3 w-20 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                      <div className="h-3 w-8 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                    </div>

                    <div className="mt-1.5 h-1.5 rounded-full bg-[var(--do-bg-2)] lirah-pulse" />
                  </div>

                  <div className="mt-2.5 border-t border-[var(--do-border)] pt-2.5">
                    <div className="h-3 w-20 rounded bg-[var(--do-bg-2)] lirah-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="au-card overflow-hidden" data-testid="shares-table">
            <div className="hidden md:block table-responsive table-scroll">
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--do-border)] bg-[var(--do-accent-soft)]">
                  <tr className="text-left text-xs uppercase tracking-[0.12em] text-[var(--do-muted)]">
                    <SortableTableHeader
                      label="Destinataire"
                      field="prospectName"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleShareSort}
                      className="px-4 py-3 font-bold"
                      testId="stats-sort-prospectName"
                    />
                    <SortableTableHeader
                      label="Engagement"
                      field="openCount"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleShareSort}
                      className="min-w-[190px] px-4 py-3 font-bold"
                      testId="stats-sort-openCount"
                    />
                    <SortableTableHeader
                      label="Documents"
                      field="documents"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleShareSort}
                      align="center"
                      className="px-4 py-3 font-bold"
                      testId="stats-sort-documents"
                    />
                    <SortableTableHeader
                      label="Temps de lecture"
                      field="totalReadingSeconds"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleShareSort}
                      className="px-4 py-3 font-bold"
                      testId="stats-sort-totalReadingSeconds"
                    />
                    <SortableTableHeader
                      label="Dernière lecture"
                      field="expiresAt"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleShareSort}
                      className="px-4 py-3 font-bold"
                      testId="stats-sort-expiresAt"
                    />
                    <SortableTableHeader
                      label="Statut"
                      field="status"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleShareSort}
                      className="px-4 py-3 font-bold"
                      testId="stats-sort-status"
                    />
                    <th className="w-40 px-4 py-3 text-right font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shares.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-12 text-center">
                        <ChartLineUp
                          size={38}
                          weight="duotone"
                          className="mx-auto mb-3 text-[var(--do-muted)]"
                        />
                        <div className="font-heading font-semibold text-[var(--do-ink)]">
                          Aucune activité trouvée
                        </div>
                        <div className="mt-1 text-xs text-[var(--do-muted)]">
                          Modifiez les filtres pour afficher d’autres partages.
                        </div>
                      </td>
                    </tr>
                  ) : null}

                  {shares.map((share) => {
                    const engagement = Math.min(
                      100,
                      Math.round(
                        (Number(share.openCount || 0) /
                          Math.max(1, Number(share.maxOpens || 1))) *
                          100
                      )
                    );
                    const status = statusForShare(share);
                    const badgeClass = status.muted
                      ? "bg-[var(--do-bg-2)] text-[var(--do-muted)] border-[var(--do-border)]"
                      : "bg-[var(--do-accent-soft)] text-[var(--do-accent)] border-[var(--do-border)]";

                    return (
                      <tr
                        key={share.id}
                        className={`border-b border-[var(--do-border)] transition ${
                          filters.shareId === share.id
                            ? "bg-[var(--do-accent-soft)] ring-1 ring-inset ring-[var(--do-accent)]"
                            : "hover:bg-[var(--do-accent-soft)]"
                        }`}
                        data-testid={`share-row-${share.id}`}
                      >
                        <td className="px-4 py-4">
                          {share.prospectId ? (
                            <Link
                              to={`/app/commerce/prospects/${share.prospectId}`}
                              className="font-semibold text-[var(--do-ink)] hover:text-[var(--do-accent)] hover:underline"
                            >
                              {share.prospectName}
                            </Link>
                          ) : (
                            <div className="font-semibold text-[var(--do-ink)]">
                              {share.prospectName}
                            </div>
                          )}
                          <div className="mt-0.5 text-xs text-[var(--do-muted)]">
                            {share.createdByName || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--do-bg-2)]">
                              <div
                                className="h-full rounded-full bg-[var(--do-accent)]"
                                style={{ width: `${engagement}%` }}
                              />
                            </div>
                            <span className="w-10 text-right font-mono-stat text-xs font-bold">
                              {engagement}%
                            </span>
                          </div>
                          <div className="mt-1 text-[11px] text-[var(--do-muted)]">
                            {share.openCount || 0}/{share.maxOpens || 0} ouvertures ·{" "}
                            {share.viewCount || 0} vues
                          </div>
                        </td>
                        <td className="max-w-[260px] px-4 py-4">
                          <div
                            className="truncate text-xs font-semibold text-[var(--do-ink)]"
                            title={
                              Array.isArray(share.snapshotDocumentNames)
                                ? share.snapshotDocumentNames.join(" · ")
                                : ""
                            }
                          >
                            {Array.isArray(share.snapshotDocumentNames) &&
                            share.snapshotDocumentNames.length > 0
                              ? share.snapshotDocumentNames.join(" · ")
                              : "—"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-[var(--do-muted)]">
                          <span className="inline-flex items-center gap-1.5">
                            <Clock size={14} />
                            {fmtSeconds(share.totalReadingSeconds || 0)}
                          </span>
                        </td>
                        <td className="px-4 py-4 font-mono-stat text-xs text-[var(--do-muted)]">
                          {fmtDate(share.lastOpenedAt)}
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`rounded-full border px-2.5 py-1 text-xs font-bold ${badgeClass}`}
                          >
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="inline-flex items-center gap-2">

                            <button
                              type="button"
                              className="acta-action-trigger stats-action-trigger !w-9 !min-w-9 !px-0 justify-center"
                              aria-label={`Actions pour ${share.prospectName}`}
                              aria-expanded={actionMenuId === share.id}
                              onClick={(event) => openActionMenu(event, share)}
                              data-testid={`stats-actions-${share.id}`}
                            >
                              <DotsThreeVertical size={17} weight="bold" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="space-y-2 p-2.5 md:hidden">
              {shares.length === 0 ? (
                <div className="p-8 text-center">
                  <ChartLineUp
                    size={36}
                    weight="duotone"
                    className="mx-auto mb-3 text-[var(--do-muted)]"
                  />
                  <div className="font-heading font-semibold">Aucune activité trouvée</div>
                </div>
              ) : null}

              {shares.map((share) => {
                const engagement = Math.min(
                  100,
                  Math.round(
                    (Number(share.openCount || 0) /
                      Math.max(1, Number(share.maxOpens || 1))) *
                      100
                  )
                );
                const status = statusForShare(share);

                return (
                  <article
                    key={share.id}
                    className="rounded-[14px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3"
                    data-testid={`share-card-${share.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        {share.prospectId ? (
                          <Link
                            to={`/app/commerce/prospects/${share.prospectId}`}
                            className="block truncate text-sm font-bold leading-tight text-[var(--do-ink)]"
                          >
                            {share.prospectName}
                          </Link>
                        ) : (
                          <div className="truncate text-sm font-bold leading-tight text-[var(--do-ink)]">
                            {share.prospectName}
                          </div>
                        )}

                        <div className="mt-0.5 truncate text-[11px] leading-tight text-[var(--do-muted)]">
                          {share.createdByName || "—"}
                        </div>
                      </div>

                      <span className="shrink-0 rounded-full border border-[var(--do-border)] px-2 py-0.5 text-[10px] font-bold leading-4 text-[var(--do-muted)]">
                        {status.label}
                      </span>
                    </div>

                    <div className="mt-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-[var(--do-muted)]">
                          Engagement
                        </span>

                        <span className="font-mono-stat text-[11px] font-bold text-[var(--do-ink)]">
                          {engagement}%
                        </span>
                      </div>

                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--do-bg-2)]">
                        <div
                          className="h-full rounded-full bg-[var(--do-accent)]"
                          style={{ width: `${engagement}%` }}
                        />
                      </div>
                    </div>

                    <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-[var(--do-border)] pt-2.5">
                      <div className="min-w-0 truncate text-[11px] text-[var(--do-muted)]">
                        Lecture{" "}
                        <span className="font-semibold text-[var(--do-ink)]">
                          {fmtSeconds(share.totalReadingSeconds || 0)}
                        </span>
                      </div>


                    </div>
                  </article>
                );
              })}
            </div>

            {totalPages > 1 ? (
              <div
                className="flex items-center justify-between gap-3 border-t border-[var(--do-border)] px-4 py-3 text-xs"
                data-testid="stats-pagination"
              >
                <div className="min-w-0 truncate text-[var(--do-muted)]">
                  Page {page} / {totalPages} · {sharesTotal} partage
                  {sharesTotal > 1 ? "s" : ""}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    disabled={page === 1}
                    data-testid="stats-page-prev"
                    className="rounded-md border border-[var(--do-border)] px-3 py-1.5 font-semibold disabled:opacity-40"
                  >
                    ←
                  </button>
                  <div className="hidden items-center gap-1 sm:flex">
                    {Array.from({ length: totalPages }, (_, index) => index + 1)
                      .slice(Math.max(0, page - 3), page + 2)
                      .map((pageNumber) => (
                        <button
                          key={pageNumber}
                          onClick={() => setPage(pageNumber)}
                          data-testid={`stats-page-${pageNumber}`}
                          className={`rounded-md px-3 py-1.5 font-semibold ${
                            pageNumber === page
                              ? "bg-[var(--do-accent)] text-[var(--do-on-accent)]"
                              : "border border-[var(--do-border)]"
                          }`}
                        >
                          {pageNumber}
                        </button>
                      ))}
                  </div>
                  <button
                    onClick={() =>
                      setPage((current) => Math.min(totalPages, current + 1))
                    }
                    disabled={page === totalPages}
                    data-testid="stats-page-next"
                    className="rounded-md border border-[var(--do-border)] px-3 py-1.5 font-semibold disabled:opacity-40"
                  >
                    →
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </section>

      <ActaActionMenu
        open={Boolean(desktopActionShare)}
        anchorEl={actionAnchorEl}
        onClose={closeActionMenus}
        width={230}
        estimatedHeight={desktopActionShare?.deleted ? 143 : 188}
        testId="stats-desktop-action-menu"
      >
        {desktopActionShare ? (
          <>
            <ActaActionMenuItem
              icon={<PencilSimple size={16} />}
              label="Modifier les ouvertures"
              description="Ajuster la limite du lien"
              onClick={() => {
                const share = desktopActionShare;
                closeActionMenus();
                openOpensDialog(share);
              }}
            />
            <ActaActionMenuItem
              icon={<CalendarBlank size={16} />}
              label="Modifier l'expiration"
              description="Changer la date de fin"
              onClick={() => {
                const share = desktopActionShare;
                closeActionMenus();
                openExpirationDialog(share);
              }}
            />
            <ActaActionMenuItem
              icon={<ArrowSquareOut size={16} />}
              label="Ouvrir le lien"
              description="Consulter le partage public"
              href={`${window.location.origin}/share/${desktopActionShare.token}`}
              target="_blank"
              rel="noreferrer"
              onClick={closeActionMenus}
            />
            {!desktopActionShare.deleted ? (
              <ActaActionMenuItem
                icon={<Trash size={16} />}
                label="Supprimer le lien"
                description="Conserver son historique"
                danger
                onClick={() => {
                  const share = desktopActionShare;
                  closeActionMenus();
                  setDeleteShareTarget(share);
                }}
              />
            ) : null}
          </>
        ) : null}
      </ActaActionMenu>

      <ActaActionSheet
        open={Boolean(mobileActionShare)}
        title="Actions"
        subtitle={mobileActionShare?.prospectName}
        onClose={closeActionMenus}
        testId="stats-mobile-action-sheet"
      >
        {mobileActionShare ? (
          <>
            <ActaActionMenuItem
              icon={<PencilSimple size={18} />}
              label="Modifier les ouvertures"
              description="Ajuster la limite du lien"
              onClick={() => {
                const share = mobileActionShare;
                closeActionMenus();
                openOpensDialog(share);
              }}
            />
            <ActaActionMenuItem
              icon={<CalendarBlank size={18} />}
              label="Modifier l'expiration"
              description="Changer la date de fin"
              onClick={() => {
                const share = mobileActionShare;
                closeActionMenus();
                openExpirationDialog(share);
              }}
            />
            <ActaActionMenuItem
              icon={<ArrowSquareOut size={18} />}
              label="Ouvrir le lien"
              description="Consulter le partage public"
              href={`${window.location.origin}/share/${mobileActionShare.token}`}
              target="_blank"
              rel="noreferrer"
              onClick={closeActionMenus}
            />
            {!mobileActionShare.deleted ? (
              <ActaActionMenuItem
                icon={<Trash size={18} />}
                label="Supprimer le lien"
                description="Conserver son historique"
                danger
                onClick={() => {
                  const share = mobileActionShare;
                  closeActionMenus();
                  setDeleteShareTarget(share);
                }}
              />
            ) : null}
          </>
        ) : null}
      </ActaActionSheet>

      <Requests embedded />

      <Dialog
        open={Boolean(editingShare)}
        onOpenChange={(open) => !open && closeEditDialog()}
      >
        <DialogContent data-testid="share-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {editMode === "opens"
                ? "Modifier les ouvertures"
                : "Modifier l'expiration"}
            </DialogTitle>
            <DialogDescription>
              {editingShare
                ? `Lien de ${editingShare.prospectName}. Le statut et l'historique de consultation sont conservés.`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {editMode === "opens" ? (
            <div>
              <label className="label-eyebrow mb-2 block">Limite d'ouvertures</label>
              <input
                type="number"
                min={1}
                max={1000}
                value={editMaxOpens}
                onChange={(event) => setEditMaxOpens(event.target.value)}
                data-testid="edit-share-max-opens"
                className="w-full rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-mono-stat"
              />
            </div>
          ) : null}

          {editMode === "expiration" ? (
            <div>
              <label className="label-eyebrow mb-2 block">
                Nouvelle date d'expiration
              </label>
              <input
                type="date"
                value={editExpirationDate}
                onChange={(event) => setEditExpirationDate(event.target.value)}
                data-testid="edit-share-expiration"
                className="w-full rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm font-mono-stat"
              />
            </div>
          ) : null}

          <DialogFooter>
            <button
              onClick={closeEditDialog}
              className="rounded-lg border border-[var(--do-border)] px-4 py-2 text-sm font-semibold"
            >
              Annuler
            </button>
            <button
              onClick={saveEdit}
              data-testid="save-share-edit"
              className="rounded-lg bg-[var(--do-accent)] px-4 py-2 text-sm font-semibold text-[var(--do-on-accent)]"
            >
              Enregistrer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ label, value, detail, icon: Icon, className = "" }) {
  return (
    <article className={`lirah-card min-w-0 p-3 sm:p-4 ${className}`}>
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <div className="font-heading text-[11px] font-semibold leading-tight text-[var(--do-muted)] sm:text-xs">
            {label}
          </div>
          <div
            className="mt-2 truncate font-heading text-lg font-black leading-tight text-[var(--do-ink)] sm:text-2xl"
            title={String(value)}
          >
            {value}
          </div>
          <div className="mt-1 truncate text-xs text-[var(--do-muted)] sm:text-sm">
            {detail}
          </div>
        </div>
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)] sm:h-9 sm:w-9">
          <Icon size={18} weight="bold" />
        </div>
      </div>
    </article>
  );
}
